import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";

const KIND_COLORS = { stock: "text-emerald-400", card: "text-sky-400", source: "text-amber-400", none: "text-zinc-500", failed: "text-red-400" };
const STATUS_LABEL = { queued: "Queued for the background worker…", running: "Re-imaging in the background worker…", done: "Finished", lost: "Lost contact — the job continues in the background" };
const STATUS_COLOR = { queued: "text-amber-300", running: "text-amber-300", done: "text-emerald-400", lost: "text-red-300" };
export const ACTIVE = ["queued", "running"];

export function Stats({ stats }) {
  return (
    <div data-testid="reimage-stats" className="flex gap-3 flex-wrap text-[11px] mono">
      {Object.entries(stats || {}).filter(([k]) => k !== "deleted_old").map(([k, v]) => (
        <span key={k} className={KIND_COLORS[k] || "text-zinc-400"}>{k} {v}</span>
      ))}
      {stats?.deleted_old ? <span className="text-zinc-500">· {stats.deleted_old} old R2 copies deleted</span> : null}
    </div>
  );
}

function ResultRow({ r }) {
  return (
    <div className="flex justify-between px-2 py-1 mono text-[11px]">
      <span className="text-zinc-300 truncate">{r.domain || r.name}</span>
      <span className="text-zinc-500">stock {r.stock} · card {r.card}{r.failed ? <span className="text-red-400"> · failed {r.failed}</span> : null}{r.snapshots ? ` · ${r.snapshots} pages refreshed` : ""}</span>
    </div>
  );
}

/** Live progress card for a re-image job (`GET /images/jobs/{id}` shape). */
export function JobCard({ job }) {
  const pct = job.total ? Math.min(100, Math.round((job.done / job.total) * 100)) : 0;
  const label = job.status === "queued" && job.queue_position > 0
    ? `Queued — ${job.queue_position} re-image job(s) ahead in the worker…`
    : STATUS_LABEL[job.status] || "Stopped";
  return (
    <div data-testid="reimage-job" className="border border-zinc-800 rounded-sm p-3 space-y-2">
      <div className="flex justify-between text-xs">
        <span data-testid="reimage-job-status" className={STATUS_COLOR[job.status] || "text-zinc-400"}>{label}</span>
        <span data-testid="reimage-progress" className="mono text-zinc-400">{job.done}/{job.total} · {pct}%</span>
      </div>
      <div className="h-1.5 bg-zinc-800 rounded-sm overflow-hidden"><div className="h-full bg-emerald-500 transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
      <Stats stats={job.stats} />
      {job.results?.length > 0 && (
        <div className="max-h-40 overflow-auto divide-y divide-zinc-900 border border-zinc-900 rounded-sm">
          {job.results.map((r) => <ResultRow key={r.site_id} r={r} />)}
        </div>
      )}
    </div>
  );
}

/** Tracks one background job: `track(id, total)` polls every 2 s until it leaves `running`. */
export function useJobPoll(onDone) {
  const [job, setJob] = useState(null);
  const timer = useRef(null);

  useEffect(() => () => clearInterval(timer.current), []);

  function track(id, total) {
    setJob({ id, status: "queued", total, done: 0, stats: {} });
    clearInterval(timer.current);
    timer.current = setInterval(async () => {
      try {
        const r = await api.get(`/images/jobs/${id}`);
        setJob(r.data);
        if (!ACTIVE.includes(r.data.status)) { clearInterval(timer.current); onDone && onDone(r.data); }
      } catch (e) {
        clearInterval(timer.current);
        setJob((j) => ({ ...j, status: "lost", poll_error: e.response?.data?.detail || e.message }));
      }
    }, 2000);
  }

  return { job, setJob, track };
}
